import React from 'react';
import { Pressable, View } from 'react-native';
import { useAppStore } from '@restorant/core';
import type { SyncState } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useT } from '../i18n';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { T } from './Text';

const ICONS: Record<SyncState, IconName> = {
  synced: 'cloudCheck',
  syncing: 'cloudUp',
  offline: 'cloudOff',
  error: 'cloudOff',
  signed_out: 'user',
  no_access: 'lock',
};

const LABEL_KEYS = {
  synced: 'sync_synced',
  syncing: 'sync_syncing',
  offline: 'sync_offline',
  error: 'sync_error',
  signed_out: 'sync_signed_out',
  no_access: 'sync_no_access',
} as const;

/**
 * Tells staff whether the till has actually saved to the cloud. Anything other
 * than "All saved" means orders are still only on this device — worth showing
 * plainly rather than hiding.
 */
export function SyncBadge({ compact = false }: { compact?: boolean }) {
  const colors = useColors();
  const t = useT();
  const sync = useAppStore((s) => s.sync);
  const syncNow = useAppStore((s) => s.syncNow);

  const tone =
    sync.state === 'synced' ? colors.good
    : sync.state === 'error' ? colors.spice
    : sync.state === 'syncing' ? colors.gold
    : colors.textFaint;

  const label = sync.pending > 0
    ? t('sync_pending', { n: sync.pending })
    : t(LABEL_KEYS[sync.state]);

  return (
    <Pressable
      onPress={() => void syncNow()}
      testID="sync-badge"
      accessibilityLabel={label}
      style={({ hovered }: any) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
        paddingVertical: 8,
        paddingHorizontal: compact ? 9 : 12,
        borderRadius: radius.pill,
        borderWidth: 1,
        borderColor: hovered ? colors.gold : colors.line,
        backgroundColor: colors.surface,
      })}
    >
      <Icon name={ICONS[sync.state]} size={15} color={tone} />
      {compact ? (
        sync.pending > 0 ? (
          <T size={11.5} weight={800} color={tone}>
            {sync.pending}
          </T>
        ) : null
      ) : (
        <T size={12} weight={700} color={tone} numberOfLines={1}>
          {label}
        </T>
      )}
    </Pressable>
  );
}
